import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Edit, Trash2, ArrowLeft, CheckCircle2, Calendar as CalendarIcon, Share2 } from 'lucide-react';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManageNoticesPage = () => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noticeToDelete, setNoticeToDelete] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    tag: 'Normal'
  });

  useEffect(() => {
    verifyAdminAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAdminAndFetch = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${BACKEND_URL}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchNotices();
    } catch (err) {
      navigate('/admin/login');
    }
  };

  const fetchNotices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notices`);
      setNotices(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching notices:', err);
      toast.error('Failed to load notices');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');

    try {
      if (editingNotice) {
        await axios.put(
          `${BACKEND_URL}/api/notices/${editingNotice.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Notice updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/notices`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Notice published successfully');
      }

      setFormData({
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        tag: 'Normal'
      });
      setShowForm(false);
      setEditingNotice(null);
      fetchNotices();
    } catch (err) {
      console.error('Error saving notice:', err);
      toast.error('Failed to save notice');
    }
  };

  const handleEdit = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      description: notice.description,
      date: notice.date,
      tag: notice.tag || 'Normal'
    });
    setShowForm(true);
  };

  const handleDeleteClick = (notice) => {
    setNoticeToDelete(notice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const token = localStorage.getItem('adminToken');

    try {
      await axios.delete(`${BACKEND_URL}/api/notices/${noticeToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notice deleted successfully');
      setDeleteDialogOpen(false);
      setNoticeToDelete(null);
      fetchNotices();
    } catch (err) {
      console.error('Error deleting notice:', err);
      toast.error('Failed to delete notice');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingNotice(null);
    setFormData({
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      tag: 'Normal'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShareOnWhatsApp = (notice) => {
    // Format the notice message for WhatsApp
    const message = `📢 *AMTEE PUBLIC SCHOOL*

*${notice.title}*
${notice.tag === 'Important' ? '⚠️ IMPORTANT NOTICE' : ''}

${notice.description}

📅 Date: ${formatDate(notice.date)}

---
AMTEE PUBLIC SCHOOL
${schoolInfo.phone}`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp with pre-filled message
    // Use web.whatsapp.com for desktop, api.whatsapp.com for mobile detection
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const whatsappUrl = isMobile 
      ? `whatsapp://send?text=${encodedMessage}`
      : `https://web.whatsapp.com/send?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={schoolInfo.logo} 
                alt="AMTEE PUBLIC SCHOOL" 
                className="h-12 w-12 object-contain"
              />
              <h1 className="text-xl font-bold text-gray-900">Manage Notices</h1>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/dashboard')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showForm ? (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                All Notices ({notices.length})
              </h2>
              <Button 
                onClick={() => setShowForm(true)}
                className="bg-[#4A90E2] hover:bg-[#357ABD]"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add New Notice
              </Button>
            </div>

            {notices.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500">No notices yet. Create your first notice!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {notices.map((notice) => (
                  <Card key={notice.id} className="shadow-md hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <CardTitle className="text-lg">{notice.title}</CardTitle>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              notice.tag === 'Important' 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {notice.tag || 'Normal'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <CalendarIcon className="h-4 w-4" />
                            <span>{formatDate(notice.date)}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleShareOnWhatsApp(notice)}
                            className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                          >
                            <Share2 className="h-4 w-4 mr-1" />
                            Share on WhatsApp
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEdit(notice)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleDeleteClick(notice)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">{notice.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">
                {editingNotice ? 'Edit Notice' : 'Create New Notice'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="title">Notice Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter notice title"
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter notice description"
                    rows={5}
                    required
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="tag">Tag *</Label>
                    <Select
                      value={formData.tag}
                      onValueChange={(value) => setFormData({ ...formData, tag: value })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select tag" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Normal">Normal</SelectItem>
                        <SelectItem value="Important">Important</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button 
                    type="submit" 
                    className="flex-1 bg-[#4A90E2] hover:bg-[#357ABD] py-6"
                  >
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    {editingNotice ? 'Update Notice' : 'Publish Notice'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleCancel}
                    className="flex-1 py-6"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the notice "{noticeToDelete?.title}". 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManageNoticesPage;
